/**
 * Shared className for the DS2026 **secondary / neutral** button — a white
 * button with a Gray-300 border, Gray-800 label, and a Gray-100 hover fill
 * (no shadow lift). This is a string copy of the DS2026 look that `FbButton`
 * renders for `variant="secondary" | "outline"` under `Ds2026ButtonProvider`
 * (see `FbButton.tsx` + the `ds2026-btn-outline-*` tokens), so plain
 * `<button>`/`<Link>` elements that can't be an `FbButton` (e.g. router links)
 * still get the genuine DS2026 button, not a lookalike.
 *
 * Icons inside should carry NO color class so they inherit the button's text
 * color — matching the campaign wizard buttons. Don't force `text-brand-active`.
 */
// NOTE: borders use `!border` / `!border-…` because an unlayered global
// bootstrap rule forces `border-color: #dee2e6` and beats Tailwind's layered
// border-color utilities. `!important` is the established workaround in this
// repo (see FbButton's secondary/disabled variants). Without it the DS2026
// Gray-300 stroke silently renders as bootstrap gray.
export const secondaryPillButtonClass =
  'inline-flex items-center justify-center gap-2 min-h-10 px-3 rounded-md text-base font-medium whitespace-nowrap transition-colors !border !border-ds2026-btn-outline-stroke bg-ds2026-btn-outline-bg text-ds2026-btn-outline-text hover:bg-ds2026-btn-outline-hover-bg hover:text-ds2026-btn-outline-hover-text'

/**
 * DS2026 **primary** button — sibling of `secondaryPillButtonClass`. Mirrors
 * the DS2026 primary look `FbButton` renders under `Ds2026ButtonProvider`
 * (`ds2026-btn-primary-*` tokens): Basil-600 fill, white label, darker hover,
 * Gray-100 disabled. Same shape/height as the secondary button so paired
 * primary/secondary rows line up.
 */
export const primaryPillButtonClass =
  'inline-flex items-center justify-center gap-2 min-h-10 px-3 rounded-md text-base font-medium whitespace-nowrap transition-colors bg-ds2026-btn-primary-bg text-ds2026-btn-primary-text hover:bg-ds2026-btn-primary-hover-bg disabled:bg-ds2026-btn-disabled-bg disabled:text-ds2026-btn-disabled-text disabled:!border disabled:!border-ds2026-btn-disabled-border'

/**
 * DS2026 **danger** button — the destructive sibling of the pill set. Same
 * shape/height as `secondaryPillButtonClass`, but an error-token outline + label
 * with a subtle error-tinted hover fill. Used for destructive header actions
 * (e.g. "Reset User Demo Data"). See `secondaryPillButtonClass` for why borders
 * use `!border`.
 */
export const dangerPillButtonClass =
  'inline-flex items-center justify-center gap-2 min-h-10 px-3 rounded-md text-base font-medium whitespace-nowrap transition-colors bg-ds2026-btn-outline-bg !border !border-error-border text-error-strong hover:bg-error-bg disabled:opacity-40 disabled:cursor-not-allowed'

/**
 * Hover/color half of `secondaryPillButtonClass`, for components that already
 * provide their own base shape (e.g. `FbDropdown`'s trigger) but want the same
 * DS2026 neutral-button border, text, and hover. Used by header dropdowns that
 * sit next to the secondary buttons so the controls read as one set.
 */
export const secondaryPillHoverClass =
  'transition-colors !border-ds2026-btn-outline-stroke text-ds2026-btn-outline-text hover:bg-ds2026-btn-outline-hover-bg hover:text-ds2026-btn-outline-hover-text'
